import { useState } from "react";
import { formatPrice } from "../utils/format";
import CatalogItem from "./CatalogItem";

// "5 элементов" / "1 элемент" / "22 элемента"
const pluralItems = (count) => {
    const mod100 = count % 100;
    const mod10 = count % 10;
    if (mod100 >= 11 && mod100 <= 14) return "элементов";
    if (mod10 === 1) return "элемент";
    if (mod10 >= 2 && mod10 <= 4) return "элемента";
    return "элементов";
};

// Сколько номенклатур внутри узла — считаем вглубь, чтобы у раздела
// в заголовке стояло число товаров, а не число подразделов.
const countItems = (node) =>
    node.items
        ? node.items.length
        : (node.children || []).reduce((sum, child) => sum + countItems(child), 0);

// Цена "от" по узлу: посчитана при группировке (groupItemsByTree), но для
// плоских групп из groupItemsByField её нет — тогда берём минимум по товарам.
const nodeMinPrice = (node) => {
    if (node.minPrice !== null && node.minPrice !== undefined) return node.minPrice;

    const prices = (node.items || [])
        .map((item) => item.minPrice)
        .filter((price) => price !== null && price !== undefined && !Number.isNaN(price));

    return prices.length ? Math.min(...prices) : null;
};

const CatalogSection = ({
    node,
    path,
    level,
    isOpen,
    onToggleSection,
    expandedName,
    onToggleExpand,
    priceBasis,
    rowQuantities,
    onQuantityChange,
    onAddRow,
}) => {
    const count = countItems(node);
    const minPrice = nodeMinPrice(node);

    return (
        <div className={`pc-group-section pc-group-level-${level}`}>
            <div
                className="pc-item-row pc-group-row"
                onClick={() => onToggleSection(path)}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen(path)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggleSection(path);
                    }
                }}
            >
                <div className="pc-item-main">
                    <div className="pc-group-title">{node.name}</div>
                    <div className="pc-item-meta">
                        {count} {pluralItems(count)}
                    </div>
                </div>
                <div className="pc-item-price">
                    <span className="pc-price-label">от</span>
                    <span className="pc-price-value">{formatPrice(minPrice)} ₽</span>
                </div>
                <div className="pc-chevron">{isOpen(path) ? "▲" : "▼"}</div>
            </div>

            {isOpen(path) && (
                <div className="pc-group-items">
                    {node.children
                        ? node.children.map((child) => (
                              <CatalogSection
                                  key={`${path}/${child.name}`}
                                  node={child}
                                  path={`${path}/${child.name}`}
                                  level={level + 1}
                                  isOpen={isOpen}
                                  onToggleSection={onToggleSection}
                                  expandedName={expandedName}
                                  onToggleExpand={onToggleExpand}
                                  priceBasis={priceBasis}
                                  rowQuantities={rowQuantities}
                                  onQuantityChange={onQuantityChange}
                                  onAddRow={onAddRow}
                              />
                          ))
                        : (node.items || []).map((group) => (
                              <CatalogItem
                                  key={`${path}-${group.name}`}
                                  group={group}
                                  isExpanded={expandedName === `${path}-${group.name}`}
                                  onToggle={() => onToggleExpand(`${path}-${group.name}`)}
                                  priceBasis={priceBasis}
                                  rowQuantities={rowQuantities}
                                  onQuantityChange={onQuantityChange}
                                  onAddRow={onAddRow}
                              />
                          ))}
                </div>
            )}
        </div>
    );
};

const CatalogGroupedList = ({
    loading,
    groups,
    expandedName,
    onToggleExpand,
    priceBasis,
    rowQuantities,
    onQuantityChange,
    onAddRow,
}) => {
    // Секции по умолчанию свёрнуты — раскрываются кликом. Держим набор открытых,
    // чтобы можно было раскрыть сразу несколько. Ключ — полный путь до узла
    // ("Кровельные материалы/Планка торцевая"), иначе одноимённые разделы
    // в разных корнях открывались бы вместе.
    const [expandedSections, setExpandedSections] = useState([]);

    const toggleSection = (path) => {
        setExpandedSections((prev) =>
            prev.includes(path)
                ? prev.filter((item) => item !== path)
                : [...prev, path]
        );
    };

    const isOpen = (path) => expandedSections.includes(path);

    if (loading) {
        return (
            <div className="pc-list">
                <div className="pc-loading">
                    <div className="pc-spinner" />
                </div>
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="pc-list">
                <div className="pc-empty">
                    Ничего не найдено по заданным фильтрам.
                </div>
            </div>
        );
    }

    return (
        <div className="pc-list">
            {groups.map((section) => (
                <CatalogSection
                    key={section.name}
                    node={section}
                    path={section.name}
                    level={0}
                    isOpen={isOpen}
                    onToggleSection={toggleSection}
                    expandedName={expandedName}
                    onToggleExpand={onToggleExpand}
                    priceBasis={priceBasis}
                    rowQuantities={rowQuantities}
                    onQuantityChange={onQuantityChange}
                    onAddRow={onAddRow}
                />
            ))}
        </div>
    );
};

export default CatalogGroupedList;
